import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { mdmIdQueryStr } from '../../common/constants/pathVariables';
import { StatusColor } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  cancelledMsg,
  companyMsg,
  contentMsg,
  customerMsg,
  deliveredMsg,
  openMsg,
  orderDateMsg,
  orderMsg,
  pendingApprovalMsg,
  statusMsg,
  t,
  unknownMsg,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatDefinedTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { SubscriptionAction } from '../../generated/api/subscriptionAction.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Grid } from '../Grid/Grid';
import { Highlight } from '../Highlight/Highlight.js';
import { Status } from '../Status/Status.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  Table,
  TableDescription,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import type { SubscriptionActionHeader } from '../../generated/api/subscriptionActionHeader.js';
import type { SubscriptionActionSearchResponse } from '../../generated/api/subscriptionActionSearchResponse.js';
import type { AuthenticatedUser } from '../../public/site/authState';

export interface RedeemRequestListProps {
  basePath: string;
  redeemRequests: SubscriptionActionSearchResponse[];
  totalItems: number;
  authenticatedUser?: AuthenticatedUser;
}
export const statusData = (status: SubscriptionActionHeader.StatusEnum): { text: string; color?: StatusColor } => {
  switch (status) {
    case SubscriptionAction.StatusEnum.COMPLETED:
      return { text: t.PQGG(deliveredMsg), color: StatusColor.GREEN };
    case SubscriptionAction.StatusEnum.PENDING:
      return { text: t.TJ54(openMsg), color: StatusColor.GREY };
    case SubscriptionAction.StatusEnum.PENDING_APPROVAL:
      return { text: t.ZXA6(pendingApprovalMsg), color: StatusColor.SALMON };
    case SubscriptionAction.StatusEnum.CANCELLED:
      return { text: t.PEKP(cancelledMsg), color: StatusColor.GREY };
    default:
      return { text: t.QWC8(unknownMsg), color: StatusColor.GREY };
  }
};

const generateRedeemRequestUrl = (path: string, redeemRequest: SubscriptionActionSearchResponse) => {
  const mdmIdStr = mdmIdQueryStr(path, redeemRequest.result.accountMasterId);
  return `${generatePath(path, { requestId: redeemRequest.result.subscriptionActionDisplayId })}${mdmIdStr}`;
};

export const RedeemRequestList = ({
  basePath,
  redeemRequests,
  totalItems,
  authenticatedUser,
}: RedeemRequestListProps) => {
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const { search } = searchParams;
  const searchTerms = search?.split(' ') || [];

  const columns = [
    {
      key: 'subscriptionDisplayId',
      label: t.C001(orderMsg),
      sortable: true,
      width: '15%',
    },
    {
      key: 'created',
      label: t.ZTPC(orderDateMsg),
      sortable: true,
      width: '10%',
    },
    {
      key: 'subscriptionContactName',
      label: t.IY5V(customerMsg),
      sortable: true,
    },
    {
      key: 'subscriptionName',
      label: t.NBNH(contentMsg),
    },
    {
      key: 'status',
      label: t.ASQT(statusMsg),
      ref: 'status',
    },
    {
      key: 'company',
      label: t.KJTS(companyMsg),
      width: '15%',
    },
  ];

  const rows = redeemRequests.map(redeemRequest => {
    const status = statusData(redeemRequest.result.status);
    return {
      subscriptionDisplayId: (
        <Link to={generateRedeemRequestUrl(basePath, redeemRequest)}>
          <TableDescription
            description={t.B951('Redemption Request')}
            title={<Highlight text={redeemRequest.result.subscriptionDisplayId ?? ''} highlights={searchTerms} />}
          />
        </Link>
      ),
      created: formatDefinedTimestampToDDMMYYYY(redeemRequest.result.created),
      subscriptionContactName: (
        <Highlight text={redeemRequest.result.subscriptionContactName ?? ''} highlights={searchTerms} />
      ),
      subscriptionName: <Highlight text={redeemRequest.result.subscriptionName ?? ''} highlights={searchTerms} />,
      status: <Status textAfterBadge={status.text} color={status.color} inTable={true} />,
      company: getCompanyName(authenticatedUser, redeemRequest.result.accountMasterId),
    };
  });

  return (
    <Grid>
      <div className={dsClass.MARGIN_TOP_4}>
        <ConsolidatedListView
          authenticatedUser={authenticatedUser}
          listElement={<Table columns={columns} noItemsText={t.SXJ5('No redeem requests')} rows={rows} />}
          listPagination={<ListPagination totalItems={totalItems} />}
          listSearch={
            <ListSearch
              onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
            />
          }
        />
      </div>
    </Grid>
  );
};
